import { useState, useEffect } from "react";
import { offeringsService } from "../../../../services/api/settings";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Star,
  Zap,
  Crown,
  ChevronDown,
  ChevronUp,
  Database,
  ArrowRight,
} from "lucide-react";
import ContactSalesModal from "./ContactSalesModal";
import DirectPlanModal from "./DirectPlanModal";
import {
  Feature,
  OfferingPlan,
  OfferingAddon,
} from "../../../../store/types/settings";
import AgreementModal from "../../misc/AgreementModal";
import Skeleton from "../../../../components/elements/Skeleton";
import formatPrice from "../../../../utility/formatPrice";
import formatPriceString0 from "../../../../utility/formatPriceString0";

interface PricingPlansProps {
  billingType: "Monthly" | "Quarterly" | "Yearly";
  orgId: string;
  selectedFeatureIds?: string[];
  selectedFeatures?: Feature[];
  onFeatureRemove?: (featureId: string) => void;
  selectedDirectAddOnIds?: string[];
  onDirectAddOnRemove?: (id: string) => void;
  selectedTier?: string | null;
  onTierSelect?: (tier: string) => void;
  showAllPlans?: boolean;
  selectedDirectPlan?: { [key: string]: string };
  onDirectPlanChange?: (plans: { [key: string]: string }) => void;
  plans?: OfferingPlan[];
  addons?: OfferingAddon[];
  loading?: boolean;
  selectedLifecyclePhaseIds?: number[];
}

export default function PricingPlans({
  billingType,
  orgId: id,
  selectedFeatureIds = [],
  selectedFeatures = [],
  onFeatureRemove,
  selectedDirectAddOnIds = [],
  onDirectAddOnRemove,
  selectedTier,
  onTierSelect,
  showAllPlans = true,
  selectedDirectPlan,
  onDirectPlanChange,
  plans: propPlans,
  addons: propAddons = [],
  loading = false,
  selectedLifecyclePhaseIds = [],
}: PricingPlansProps) {
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const navigate = useNavigate();

  const [localSelectedDirectPlan, setLocalSelectedDirectPlan] = useState<{
    [key: string]: string;
  }>({});

  const activeSelectedDirectPlan =
    selectedDirectPlan || localSelectedDirectPlan;

  const updateSelectedDirectPlan = (newPlanList: { [key: string]: string }) => {
    if (onDirectPlanChange) onDirectPlanChange(newPlanList);
    else setLocalSelectedDirectPlan(newPlanList);
  };

  // Track setup fees per AI plan tier (keyed by the AI plan tier e.g. PROFESSIONAL)
  const [directPlanSetupFees, setDirectPlanSetupFees] = useState<{
    [key: string]: number;
  }>({});

  const [directModalState, setDirectModalState] = useState<{
    isOpen: boolean;
    planIndex: number | null;
  }>({
    isOpen: false,
    planIndex: null,
  });

  const selectedAddOns = (propAddons || []).filter((addon) =>
    selectedDirectAddOnIds.includes(addon.id),
  );

  const [agreementModalState, setAgreementModalState] = useState<{
    isOpen: boolean;
    planTier?: string;
    planName?: string;
  }>({
    isOpen: false,
  });

  const handleAgreementConfirm = () => {
    if (!agreementModalState.planTier) return;

    const featureIdsParam =
      selectedFeatureIds.length > 0
        ? `&featureIds=${selectedFeatureIds.join(",")}`
        : "";

    const directAddOnIdsParam =
      selectedDirectAddOnIds.length > 0
        ? `&directAddOnIds=${selectedDirectAddOnIds.join(",")}`
        : "";

    const directPlanParam = activeSelectedDirectPlan[
      agreementModalState.planTier
    ]
      ? `&directPlan=${activeSelectedDirectPlan[agreementModalState.planTier]}&includeSetupFee=true`
      : "";

    const targetTier =
      agreementModalState.planTier || selectedTier || "STARTER";

    const lifecycleParam =
      selectedLifecyclePhaseIds.length > 0
        ? `&lifecyclePhaseIds=${selectedLifecyclePhaseIds.join(",")}`
        : "";

    navigate(
      `/pay?Id=${id}&tier=${targetTier}${directPlanParam}${featureIdsParam}${directAddOnIdsParam}${lifecycleParam}`,
    );

    setAgreementModalState({ isOpen: false });
  };

  const [directPlans, setDirectPlans] = useState<any[]>([
    {
      name: "Starter",
      subtitle: "Small Teams",
      description:
        "Best for Small Agencies & Startups building their first database.",
      monthlyPrice: 689000,
      yearlyPrice: 75790000,
      setupFee: "4,350,000",
      features: [
        "CV Capacity: Up to 10,000 Candidates",
        "User Seats: 5 Users",
        "Parsing Engine: Standard Text Extraction",
        "Job Matching: Keyword Matching",
        "Search Capabilities: Basic Filters",
        "Export/Reporting: Standard CSV Exports",
        "Support SLA: Email Support (48h response)",
        "Software Updates: Quarterly Security Patches",
      ],
      tier: "STARTER",
      integrationServices: [
        {
          name: "Project Kick-off and Requirements Discovery Workshop",
          cost: "500,000",
        },
        {
          name: "Standardized Onboarding and Comprehensive 2-Day Training",
          cost: "800,000",
        },
        { name: "Basic Data Migration (up to 5,000 records)", cost: "500,000" },
        { name: "Email-based CV Parsing", cost: "400,000" },
        { name: "Career Page Webhook Integration", cost: "450,000" },
        { name: "Basic AI Scoring Dashboard", cost: "500,000" },
        { name: "Regulatory Compliance Setup", cost: "500,000" },
        { name: "Go-Live Production Support (1-week)", cost: "400,000" },
        { name: "Project Management Oversight", cost: "300,000" },
      ],
    },
    {
      name: "Growth",
      subtitle: "Mid-Sized Companies",
      description:
        "Best for Mid-Sized Companies requiring automation and scanned document processing.",
      monthlyPrice: 1034000,
      yearlyPrice: 11374000,
      setupFee: "5,200,000",
      features: [
        "CV Capacity: Up to 100,000 Candidates",
        "User Seats: 20 Users",
        "Parsing Engine: Text + OCR",
        "Job Matching: AI Scoring & Ranking",
        "Search Capabilities: Boolean Search + Tags",
        "Export/Reporting: Custom Reporting Dashboard",
        "Support SLA: Priority Email (12h response)",
        "Software Updates: Monthly Feature Updates",
      ],
      tier: "GROWTH",
      integrationServices: [
        { name: "RAG Semantic Search & Vector DB", cost: "900,000" },
        { name: "Full AI Hiring Pipeline", cost: "1,200,000" },
        { name: "Enhanced Data Migration (up to 20k)", cost: "1,000,000" },
        { name: "Self-Service Portal Setup", cost: "600,000" },
        { name: "Extended Support (2 weeks)", cost: "600,000" },
        { name: "Predictive Hiring Analytics", cost: "500,000" },
        { name: "Implementation Lead", cost: "400,000" },
      ],
    },
    {
      name: "Scale",
      subtitle: "Large Corps / Staffing Firms",
      description:
        "Best for Large Corporations & Staffing Firms needing unlimited scale and deep analytics.",
      monthlyPrice: 2000000,
      yearlyPrice: 22000000,
      setupFee: "22,400,000",
      features: [
        "CV Capacity: Unlimited Database Size",
        "User Seats: Unlimited Users",
        "Parsing Engine: Text + OCR + Multilingual",
        "Job Matching: AI + Skill Gap Analysis",
        "Search Capabilities: Advanced + Semantic",
        "Export/Reporting: Full Analytics Suite",
        "Support SLA: Dedicated Account Manager",
        "Software Updates: Early Access & Beta Features",
      ],
      tier: "SCALE",
      integrationServices: [
        { name: "ERP / HRIS Custom Connector", cost: "3,000,000" },
        { name: "Utility Workforce Module", cost: "2,800,000" },
        { name: "On-Premise / Private Cloud Deployment", cost: "2,500,000" },
        { name: "Full White-Label Branding", cost: "2,500,000" },
        { name: "Historical Data Loading + AI Retraining", cost: "1,500,000" },
        { name: "Advanced Data Migration (to 1m)", cost: "1,200,000" },
        { name: "99.5% Uptime SLA + Quarterly Audits", cost: "2,500,000" },
        { name: "Extended Hypercare Support (4 weeks)", cost: "800,000" },
        { name: "Senior Project Manager", cost: "800,000" },
        {
          name: "Enhanced Data Migration (for up to 100,000 records)",
          cost: "800,000",
        },
        { name: "Up to 200 AI-powered interviews / month", cost: "4,000,000" },
      ],
    },
    {
      name: "Enterprise",
      subtitle: "Governments & Multinationals",
      description:
        "The absolute top tier for Conglomerates & Governments requiring bespoke AI and multi-tenancy.",
      monthlyPrice: 0,
      yearlyPrice: 0,
      setupFee: "Custom",
      features: [
        "CV Capacity: Unlimited + Multi-Tenancy",
        "User Seats: Unlimited + SSO Integration",
        "Parsing Engine: Bespoke Custom Parsing",
        "Job Matching: Custom AI Model Training",
        "Infrastructure: High Availability (HA) Cluster",
        "Security: Military-grade encryption",
        "Support SLA: Dedicated Engineering Team",
        "Software Updates: Controlled Release",
      ],
      contactSales: true,
      tier: "ENTERPRISE",
      integrationServices: [
        { name: "Bespoke Implementation", cost: "Contact Sales" },
      ],
    },
  ]);

  useEffect(() => {
    const fetchDirectPlans = async () => {
      try {
        const response = await offeringsService.getAll();
        if (response.status === "success" && response.data?.DIRECT?.plans) {
          const directTierOrder = ["STARTER", "GROWTH", "SCALE", "ENTERPRISE"];
          const sorted = [...response.data.DIRECT.plans].sort((a, b) => {
            const indexA = directTierOrder.indexOf(a.tier || "");
            const indexB = directTierOrder.indexOf(b.tier || "");
            return indexA - indexB;
          });

          const formatted = sorted.map((plan) => ({
            name: plan.title,
            subtitle: plan.subtitle,
            description: plan.description,
            monthlyPrice: parseFloat(plan.monthlyPrice) || 0,
            yearlyPrice: parseFloat(plan.yearlyPrice) || 0,
            setupFee: (
              parseFloat(plan.setupFee || plan.integrationCost || "0") || 0
            ).toLocaleString(),
            features: plan.points || [],
            tier: plan.tier || "",
            contactSales:
              plan.tier === "ENTERPRISE" || parseFloat(plan.monthlyPrice) === 0,
            integrationServices:
              (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
                ? plan
                    .costBreakdown!.deploymentCosts.filter(
                      (d: any) => d && d.name,
                    )
                    .map((d: any) => ({
                      name: d.name,
                      cost: d.price.toLocaleString(),
                    }))
                : plan.setupFee || plan.integrationCost
                  ? [
                      {
                        name: "Standard Onboarding & Training",
                        cost: (
                          parseFloat(
                            plan.setupFee || plan.integrationCost || "0",
                          ) || 0
                        ).toLocaleString(),
                      },
                    ]
                  : [],
          }));
          setDirectPlans(formatted);
        }
      } catch (error) {
        console.error("Error fetching direct plans:", error);
      }
    };
    fetchDirectPlans();
  }, []);

  const handleAddDirectPlan = (tier: string, setupFee: number) => {
    const mainPlanTier = plans[directModalState.planIndex!].tier!;
    updateSelectedDirectPlan({
      ...activeSelectedDirectPlan,
      [mainPlanTier]: tier,
    });
    // Store setup fee keyed by the Direct plan tier
    setDirectPlanSetupFees((prev) => ({ ...prev, [mainPlanTier]: setupFee }));
    setDirectModalState({ isOpen: false, planIndex: null });
  };

  const handleRemoveDirectPlan = (mainPlanTier: string) => {
    const newState = { ...activeSelectedDirectPlan };
    delete newState[mainPlanTier];
    updateSelectedDirectPlan(newState);
    setDirectPlanSetupFees((prev) => {
      const next = { ...prev };
      delete next[mainPlanTier];
      return next;
    });
    setDirectModalState({ isOpen: false, planIndex: null });
  };

  const calculateTotalPrice = (plan: any): string => {
    const basePriceStr =
      billingType === "Monthly"
        ? plan.priceMonthly
        : billingType === "Quarterly"
          ? plan.priceQuarterly ||
            (
              parseFloat(plan.priceMonthly.replace(/[₦,]/g, "")) * 3
            ).toLocaleString()
          : plan.priceYearly;
    const basePrice =
      parseFloat(String(basePriceStr).replace(/[₦,]/g, "")) || 0;
    const featuresTotal = selectedFeatures.reduce((sum, f) => sum + f.price, 0);
    const addOnsTotal = (selectedAddOns || []).reduce(
      (sum, addon) =>
        sum +
        (typeof addon.price === "string"
          ? parseFloat(addon.price)
          : addon.price || 0),
      0,
    );
    let integrationCost = 0;
    if (plan.integration && plan.integration !== "Custom") {
      integrationCost = parseFloat(plan.integration.replace(/[₦,]/g, "")) || 0;
    }
    // Use stored setup fee from modal (sum of integration services costs)
    const directSetupFee = directPlanSetupFees[plan.tier] || 0;
    let directPlanCost = 0;
    const selectedDirectTier = activeSelectedDirectPlan[plan.tier];
    if (selectedDirectTier) {
      const dPlan = directPlans.find((p) => p.tier === selectedDirectTier);
      if (dPlan) {
        const dPrice =
          billingType === "Monthly"
            ? dPlan.monthlyPrice
            : billingType === "Quarterly"
              ? dPlan.quarterlyPrice || dPlan.monthlyPrice * 3
              : dPlan.yearlyPrice;
        directPlanCost = (dPrice || 0) + directSetupFee;
      }
    }
    const total =
      basePrice +
      // (basePrice ) +
      featuresTotal +
      addOnsTotal +
      integrationCost +
      directPlanCost;
    return `${total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getTierIcon = (tier: string | undefined) => {
    switch (tier?.toUpperCase()) {
      case "STARTER":
        return Zap;
      case "GROWTH":
        return Star;
      case "SCALE":
        return Crown;
      case "ENTERPRISE":
        return Crown;
      default:
        return Zap;
    }
  };

  const plans = (propPlans || [])?.map((plan: OfferingPlan) => {
    const isVipPlan =
      plan.tier === "ENTERPRISE" ||
      parseFloat(plan.monthlyPrice) === 0 ||
      plan.monthlyPrice === "0";
    return {
      ...plan,
      name: plan.title,
      badge: plan.subtitle,
      icon: getTierIcon(plan.tier || ""),
      popular: plan.tier === "ENTERPRISE",
      isVip: isVipPlan,
      priceMonthly: parseFloat(plan.monthlyPrice).toLocaleString(),
      priceQuarterly: plan.quarterlyPrice
        ? parseFloat(String(plan.quarterlyPrice)).toLocaleString()
        : (parseFloat(plan.monthlyPrice) * 3).toLocaleString(),
      priceYearly: parseFloat(plan.yearlyPrice).toLocaleString(),
      integration: parseFloat(
        plan.setupFee || plan.integrationCost || "0",
      ).toLocaleString(),
      integrationServices:
        (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
          ? plan
              .costBreakdown!.deploymentCosts.filter((d: any) => d && d.name)
              .map((d: any) => ({
                name: d.name,
                cost: d.price.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }),
              }))
          : plan.setupFee || plan.integrationCost
            ? [
                {
                  name: "Standard Onboarding & Training",
                  cost: (
                    parseFloat(plan.setupFee || plan.integrationCost || "0") ||
                    0
                  ).toLocaleString(),
                },
              ]
            : [],
      button:
        plan.tier === "ENTERPRISE" || isVipPlan ? "Contact Sales" : "Get Now",
      features: plan.points || [],
    };
  });

  return (
    <>
      <ContactSalesModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
      <section className="font-abel md:pt-10 md:pb-20 pt-7 pb-7 bg-gradient-to-b from-white to-[#7f7f7f]/0">
        <div className="max-w-7xl mx-auto px-3 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading
              ? Array.from({ length: 4 })?.map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-[20px] p-8 border border-[#EEEEEE] shadow-lg flex flex-col h-[600px]"
                  >
                    <Skeleton
                      variant="rectangular"
                      height={32}
                      width="60%"
                      className="mb-4"
                    />
                    <Skeleton
                      variant="text"
                      height={20}
                      width="80%"
                      className="mb-6"
                    />
                    <div className="mb-8">
                      <Skeleton
                        variant="rectangular"
                        height={48}
                        width="100%"
                        className="mb-2"
                      />
                      <Skeleton variant="text" height={14} width="40%" />
                    </div>
                    <div className="space-y-4 mb-8 flex-1">
                      {Array.from({ length: 6 })?.map((_, j) => (
                        <Skeleton
                          key={j}
                          variant="text"
                          height={14}
                          width="90%"
                        />
                      ))}
                    </div>
                    <Skeleton
                      variant="rectangular"
                      height={48}
                      width="100%"
                      className="rounded-xl mt-auto"
                    />
                  </div>
                ))
              : plans
                  .filter((p: any) => showAllPlans || p.tier === selectedTier)
                  ?.map((plan: any, index: number) => {
                    const isCustom =
                      plan.priceMonthly === "Contact Us" ||
                      plan.priceMonthly === "0" ||
                      parseFloat(plan.monthlyPrice) === 0 ||
                      plan.tier === "ENTERPRISE" ||
                      plan.isVip;
                    const isExpanded = expandedPlan === index;
                    const isSelected = selectedTier === plan.tier;

                    return (
                      <div
                        key={index}
                        className={`relative rounded-[20px] transition-all duration-300 hover:shadow-2xl border ${
                          plan.isVip
                            ? "bg-gradient-to-br from-[#0B60D6] to-[#1976D2] border-[#1E88E5] shadow-lg"
                            : plan.popular
                              ? "bg-white border-[#2196F3] shadow-xl md:scale-105"
                              : "bg-white border-[#EEEEEE] shadow-lg"
                        } ${isExpanded ? "ring-2 ring-[#42A5F5]" : ""}`}
                      >
                        {plan.popular && (
                          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                            <div className="px-4 py-1 bg-gradient-to-r from-[#1976D2] to-[#0B60D6] text-white text-sm font-medium rounded-full shadow-lg">
                              Most Popular
                            </div>
                          </div>
                        )}

                        <div className="p-5">
                          <div className="mb-4">
                            <h3
                              className={`text-xl font-normal mb-2 ${plan.isVip ? "text-white" : "text-[#212121]"}`}
                            >
                              {plan.name}
                            </h3>
                            <span
                              className={`inline-block px-3 py-1 text-sm font-medium rounded-full border border-[#1976D2] ${plan.isVip ? "text-white bg-[#1976D2]/40" : "text-[#1976D2] bg-white/30"}`}
                            >
                              {plan.badge}
                            </span>
                          </div>

                          <div className="mb-4">
                            {isCustom ? (
                              <div
                                className={`text-2xl font-medium mb-1 ${plan.isVip ? "text-white" : "text-[#212121]"}`}
                              >
                                Contact Us
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center">
                                  <div
                                    className={`text-2xl font-medium mb-1 flex ${plan.isVip ? "text-white" : "text-[#212121]"}`}
                                  >
                                    <span className="text-[13px] font-[300] pt-1">
                                      N
                                    </span>
                                    {billingType === "Monthly"
                                      ? formatPriceString0(plan.monthlyPrice)
                                      : billingType === "Quarterly"
                                        ? formatPriceString0(
                                            plan.priceQuarterly ||
                                              (
                                                parseFloat(
                                                  plan.monthlyPrice || "0",
                                                ) * 3
                                              ).toString(),
                                          )
                                        : formatPriceString0(plan.yearlyPrice)}
                                  </div>
                                  <div
                                    className={`text-[12px] ml-1 ${plan.isVip ? "text-blue-100" : "text-gray-600"}`}
                                  >
                                    /
                                    {billingType === "Monthly"
                                      ? "month"
                                      : billingType === "Quarterly"
                                        ? "quarter"
                                        : "year"}
                                  </div>
                                </div>
                                <div
                                  className={`mt-2 text-[12px] font-medium ${plan.isVip ? "text-blue-100" : "text-[#616161]"}`}
                                >
                                  Setup fee:{" "}
                                  <span
                                    className={
                                      plan.isVip ? "text-white" : "text-black"
                                    }
                                  >
                                    {plan.integration}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>

                          <button
                            onClick={() => {
                              if (plan.isVip) setShowContactModal(true);
                              else
                                setAgreementModalState({
                                  isOpen: true,
                                  planTier: plan.tier,
                                  planName: plan.name,
                                });
                            }}
                            className={`w-full py-3 px-6 rounded-lg transition-all flex items-center justify-center mb-4 ${
                              plan.isVip
                                ? "bg-white text-[#0B60D6] hover:bg-gray-100"
                                : plan.popular
                                  ? "bg-gradient-to-r from-[#1976D2] to-[#0B60D6] text-white shadow-md hover:scale-105"
                                  : "bg-[#1E88E5] text-white hover:bg-[#1976D2]"
                            }`}
                          >
                            <span className="pr-1">{plan.button}</span>
                            <ArrowRight className="w-4 h-4" />
                          </button>

                          {/* Moving features to sidebar as per requirement */}
                          {!selectedTier && (
                            <ul className="space-y-3 mb-8 flex-1">
                              {plan?.features?.map(
                                (feature: string, idx: number) => (
                                  <li
                                    key={idx}
                                    className={`flex items-start gap-3 text-sm ${plan.isVip ? "text-blue-50" : "text-gray-600"}`}
                                  >
                                    <Check
                                      className={`w-4 h-4 mt-1 flex-shrink-0 ${plan.isVip ? "text-white" : "text-blue-600"}`}
                                    />
                                    {feature}
                                  </li>
                                ),
                              )}
                            </ul>
                          )}

                          <div className="flex-1"></div>

                          <div
                            className={`mt-auto pt-6 border-t ${plan.isVip ? "border-white/20" : "border-gray-100"}`}
                          >
                            <div className="flex flex-col gap-2">
                              {activeSelectedDirectPlan[plan.tier] && (
                                <span className="flex items-center gap-1 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium self-start mb-1">
                                  <Check className="w-2.5 h-2.5" /> ADDED
                                </span>
                              )}
                              <button
                                onClick={() =>
                                  setDirectModalState({
                                    isOpen: true,
                                    planIndex: index,
                                  })
                                }
                                className={`flex items-center justify-between p-3 rounded-xl border transition-all group ${
                                  activeSelectedDirectPlan[plan.tier]
                                    ? "border-green-500 bg-green-50/50"
                                    : plan.isVip
                                      ? "border-white/30 hover:bg-white/10"
                                      : "border-gray-100 hover:border-blue-300 hover:bg-blue-50/30"
                                }`}
                              >
                                <div className="flex items-center gap-3 text-left">
                                  <div
                                    className={`p-2 rounded-lg ${plan.isVip ? "bg-white/20" : "bg-blue-100"}`}
                                  >
                                    <Database
                                      className={`w-4 h-4 ${plan.isVip ? "text-white" : "text-blue-600"}`}
                                    />
                                  </div>
                                  <div>
                                    <p
                                      className={`text-sm font-medium ${plan.isVip ? "text-white" : "text-[#616161]"}`}
                                    >
                                      {directPlans[index]?.name ||
                                        "Direct Add-on"}
                                    </p>
                                  </div>
                                </div>
                                <ChevronUp
                                  className={`w-4 h-4 rotate-90 transform group-hover:translate-x-1 ${plan.isVip ? "text-white" : "text-gray-500"}`}
                                />
                              </button>
                            </div>
                          </div>

                          <button
                            onClick={() =>
                              setExpandedPlan(isExpanded ? null : index)
                            }
                            className={`w-full py-2.5 px-3 mt-4 border rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                              plan.isVip
                                ? "border-white/30 text-white hover:bg-white/10"
                                : "border-gray-100 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                            <span className="text-sm">
                              {isExpanded ? "Hide Details" : "View Details"}
                            </span>
                          </button>

                          {isExpanded &&
                            plan.integrationServices &&
                            plan.integrationServices.length > 0 && (
                              <div
                                className={`mt-4 pt-4 border-t ${plan.isVip ? "border-white/20" : "border-gray-100"}`}
                              >
                                <div className="flex justify-between mb-3 text-sm font-medium">
                                  <span
                                    className={
                                      plan.isVip
                                        ? "text-white"
                                        : "text-gray-900"
                                    }
                                  >
                                    Integration Services
                                  </span>
                                  <span
                                    className={
                                      plan.isVip
                                        ? "text-white"
                                        : "text-gray-900"
                                    }
                                  >
                                    Cost (NGN)
                                  </span>
                                </div>
                                <ul className="space-y-2">
                                  {plan?.integrationServices
                                    ?.filter(Boolean)
                                    ?.map((service: any, i: number) => (
                                      <li
                                        key={i}
                                        className="flex justify-between text-xs"
                                      >
                                        <span
                                          className={
                                            plan.isVip
                                              ? "text-blue-100"
                                              : "text-gray-600"
                                          }
                                        >
                                          {service?.name || "Service"}
                                        </span>
                                        <span
                                          className={
                                            plan.isVip
                                              ? "text-white"
                                              : "text-gray-900"
                                          }
                                        >
                                          {service?.cost || "0"}
                                        </span>
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
          </div>
        </div>
      </section>

      {directModalState.isOpen &&
        directModalState.planIndex !== null &&
        directPlans[directModalState.planIndex] && (
          <DirectPlanModal
            isOpen={directModalState.isOpen}
            onClose={() =>
              setDirectModalState({ isOpen: false, planIndex: null })
            }
            plan={directPlans[directModalState.planIndex]}
            onGetStarted={handleAddDirectPlan}
            billingType={billingType}
            isSelected={
              activeSelectedDirectPlan[
                plans[directModalState.planIndex].tier!
              ] === directPlans[directModalState.planIndex].tier
            }
            onRemove={() =>
              handleRemoveDirectPlan(plans[directModalState.planIndex!].tier!)
            }
          />
        )}

      {agreementModalState.isOpen && (
        <AgreementModal
          isOpen={agreementModalState.isOpen}
          onClose={() => setAgreementModalState({ isOpen: false })}
          onConfirm={handleAgreementConfirm}
          billingType={billingType}
          selectedFeatures={selectedFeatures}
          selectedDirectAddOnIds={selectedDirectAddOnIds}
          link={`/agreement?Id=${id}&tier=${agreementModalState.planTier}`}
          title={agreementModalState.planName || ""}
          tier={agreementModalState.planTier || ""}
          id={id}
        />
      )}
    </>
  );
}
